import * as d3 from 'd3'
import * as React from 'react'
import styled from 'styled-components'
import { BodyText } from '../../../../common/src/components/core'
import LineChart, { LineChartConfig } from './LineChart'

export interface LabelledDateVal {
  series: string
  date: string
  value: number
}

export interface LabelledSeries {
  displayName?: string
  name: string
  color: string
  darkColor: string
}

export interface LabelledLineChartData {
  series: LabelledSeries[]
  values: LabelledDateVal[]
}

export type LabelledLineChartConfig = Partial<LineChartConfig<LabelledDateVal>> & {
  legendFormatter?: (value: number) => string
}

type LabelledLineChartFullConfig = LineChartConfig<LabelledDateVal> & {
  legendFormatter: (value: number) => string
}

const DefaultConfig = {
  dateMapper(row: LabelledDateVal) {
    return row.date
  },
  valueMapper(row: LabelledDateVal) {
    return row.value
  },
  seriesMapper(row: LabelledDateVal) {
    return row.series
  },
  yLabelFormatter: d3.format('.1%'),
  legendFormatter: d3.format('.2%'),
  tooltipFormatter: d3.timeFormat('%b, %Y'),
}

/**
 * Creates a LineChart with a legend.
 */
export default class LabelledLineChart extends React.PureComponent<
  {
    config: LabelledLineChartConfig
    data: LabelledLineChartData
    dateRange?: [string, string]
    onHover?: (date?: string) => void
  },
  { hoverDate?: string }
> {
  private memoData?: LabelledLineChartData
  private seriesColors?: { [seriesName: string]: string }
  private seriesValues?: { [seriesName: string]: { [date: string]: number } }

  private memoConfig?: LabelledLineChartConfig
  private fullConfig?: LabelledLineChartFullConfig

  constructor(props: LabelledLineChart['props']) {
    super(props)
    this.state = {
      hoverDate: undefined,
    }
  }

  render() {
    const { data, dateRange, config } = this.props

    // Memoize for speed
    if (data !== this.memoData) {
      const seriesColors: any = {}
      const seriesValues: any = {}
      data.series.forEach(series => {
        seriesColors[series.name] = series.color
        seriesValues[series.name] = {}
      })
      data.values.forEach(row => {
        seriesValues[row.series][row.date] = row.value
      })
      this.seriesColors = seriesColors
      this.seriesValues = seriesValues
      this.memoData = data
    }

    if (config !== this.memoConfig) {
      this.fullConfig = Object.assign({}, DefaultConfig, config, {
        colorMapper: (seriesName: string) => this.getColorForSeries(seriesName),
      })
      this.memoConfig = config
    }

    return (
      <LabelledLineChartWrap>
        <LineChart data={data.values} dateRange={dateRange} config={this.fullConfig!} onHover={this.handleHoverBound} />
        {this.renderLegend()}
      </LabelledLineChartWrap>
    )
  }

  private renderLegend() {
    const { data } = this.props
    const { hoverDate } = this.state

    const seriesValues = this.seriesValues!
    const { legendFormatter } = this.fullConfig!
    const keys = data.series.map(series => {
      const vals = seriesValues[series.name]
      if (vals == null) {
        throw new Error('Missing vals for ' + series.name)
      }

      const val = hoverDate != null ? vals[hoverDate] : null
      const valStr = val == null ? '' : legendFormatter(val)

      return (
        <KeyWrap key={series.name}>
          <KeyLabel>{series.displayName || series.name}</KeyLabel>
          <KeyIcon style={{ backgroundColor: series.darkColor }}>
            <KeyIconDot style={{ backgroundColor: series.color }} />
          </KeyIcon>
          <KeyValue style={{ color: series.color }}>{valStr}</KeyValue>
        </KeyWrap>
      )
    })

    return <LegendWrap>{keys}</LegendWrap>
  }

  private getColorForSeries(seriesName: string) {
    if (!this.seriesColors) {
      throw new Error('_seriesColors not set')
    }
    return this.seriesColors[seriesName]
  }

  private handleHoverBound = (date?: string) => {
    if (this.props.onHover) {
      this.props.onHover(date)
    }
    this.setState({ hoverDate: date })
  }
}

const LabelledLineChartWrap = styled.div``

const LegendWrap = styled.div`
  display: flex;
  margin: 0;
  justify-content: space-between;
  flex-basis: 100%;
  flex-wrap: wrap;
  max-width: 480px;
`

const KeyWrap = styled(BodyText)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 4px 0px;
`

const KeyLabel = styled.div`
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const KeyIcon = styled.div`
  width: 24px;
  height: 4px;
  margin: 0px 8px;
  border-radius: 8px;
  position: relative;
  top: -2px;
`

const KeyIconDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 8px;
  position: absolute;
  left: 8px;
  top: -2px;
`

const KeyValue = styled.div`
  min-width: 64px;
`
